// Peak Adventures Malnad — site constants
import { mediaUrl } from "@/data/media";

export const SITE = {
  brand: "Peak Adventures Malnad",
  tagline: "Your gateway to the best of Malnad",
  phoneE164: "917795677115",
  phoneDisplay: "+91 77956 77115",
  email: "peakadventuresmalnad@gmail.com",
  regions: "Chikkamagaluru · Mudigere · Kalasa · Sakleshpur",
  address: "Mudigere, Chikkamagaluru District, Karnataka 577132, India",
  domain: "https://peakadventuresmalnad.in",
  // Media (Cloudinary-first, local fallback)
  get logo()        { return mediaUrl("logoNav"); },
  get logoFooter()  { return mediaUrl("logoFooter"); },
  get heroVideo()   { return mediaUrl("heroVideo"); },   // used as fallback path — actual Cloudinary URL comes from CloudinaryVideo component
  get heroPoster()  { return mediaUrl("heroPoster"); },
  // Public IDs (for reusable components)
  cloudinary: {
    logoNav: "logo/nav-logo",
    logoFooter: "logo/footer-logo",
    favicon: "icons/favicon",
    heroVideo: "hero/drone-view",
    heroPoster: "hero/poster",
  },
  instagram: "https://www.instagram.com/peakadventuresmalnad?igsh=aDZ2aGlsMmpsam9y",
  creditName: "Sunil",
  creditUrl: "https://smartmarketingchikmagalur.netlify.app",
  whatsappMessage:
    "Hi Peak Adventures Malnad, I'd like to plan a Malnad stay. Please share partner-homestay availability and packages.",
};

// Public-ID map for CloudinaryImage components (per section)
export const CLOUDINARY_IDS = {
  aboutAmbience: "about/ambience",
  aboutEstate: "about/estate",
  diningTable: "food/dining-table",
  dishNeerDosa: "food/neer-dosa",
  dishKadubu: "food/kadubu",
  campfire: "bonfire/main",
  roomCouple: "rooms/couple",
  roomFamily: "rooms/family",
  roomGroup: "rooms/group",
  roomDorm: "rooms/dormitory",
};

// Legacy IMG helper — auto-routes through Cloudinary when configured
export const IMG = {
  get hero()          { return mediaUrl("heroPoster"); },
  get aboutAmbience() { return mediaUrl("aboutAmbience"); },
  get aboutEstate()   { return mediaUrl("aboutEstate"); },
  get diningTable()   { return mediaUrl("diningTable"); },
  get campfire()      { return mediaUrl("campfire"); },
  get roomCouple()    { return mediaUrl("roomCouple"); },
  get roomFamily()    { return mediaUrl("roomFamily"); },
  get roomGroup()     { return mediaUrl("roomGroup"); },
  get roomBunk()      { return mediaUrl("roomDorm"); },
  get dishNeerDosa()  { return mediaUrl("dishNeerDosa"); },
  get dishKadubu()    { return mediaUrl("dishKadubu"); },
};

export const ROOMS = [
  {
    id: "couple",
    name: "Couple Cottage",
    tag: "Private & romantic",
    img: IMG.roomCouple,
  },
  {
    id: "family",
    name: "Family Cottage",
    tag: "Warm & spacious",
    img: IMG.roomFamily,
  },
  {
    id: "group",
    name: "Group Cottage",
    tag: "Together, in style",
    img: IMG.roomGroup,
  },
  {
    id: "dormitory",
    name: "Dormitory",
    tag: "For travellers & tribes",
    img: IMG.roomBunk,
  },
];

export const CUISINE_MENU = [
  "Akki Rotti",
  "Kadubu",
  "Koli Saaru (Chicken Curry)",
  "Bisi Bele Bath",
  "Ragi Dosa",
  "Filter Coffee",
  "Neer Dosa",
  "Fresh Malnad Vegetarian Meals",
  "Traditional Banana Leaf Meals",
];

// Cuisine carousel (image + caption) — replaced the static 2-card layout.
export const CUISINE_GALLERY = [
  {
    id: "malnad-thali",
    name: "Malnad Special Thali",
    subtitle: "A plate that tells a story",
    body: "Rice, chicken curry, boiled egg, ghee, chutneys and papad — the classic Malnad thali served on banana leaf.",
    img: "/food-1.jpg",
  },
  {
    id: "banana-leaf-meal",
    name: "Banana Leaf Meal",
    subtitle: "Homegrown, hand-served",
    body: "Rice, sambar, coconut chutney, fried fish, curry and papad — cooked with produce from partner estates.",
    img: "/food-2.jpg",
  },
  {
    id: "kadubu-koli-saaru",
    name: "Kadubu & Koli Saaru",
    subtitle: "Malnad classic",
    body: "Steamed rice dumplings served with a fiery chicken curry — one of the most loved Malnad combinations.",
    img: "/food-5.jpg",
  },
  {
    id: "akki-rotti-curry",
    name: "Akki Rotti",
    subtitle: "Rice-flour flatbread",
    body: "Warm, hand-pressed akki rotti served with a slow-cooked estate curry and coconut chutney — a Malnad breakfast staple.",
    img: "/food-6.jpg",
  },
  {
    id: "neer-dosa",
    name: "Neer Dosa",
    subtitle: "House favourite",
    body: "Coastal-style rice crêpes served on banana leaves with fresh coconut chutney and Malnad chicken sukka.",
    img: "/dish-neerdosa.jpg",
  },
  {
    id: "kadubu",
    name: "Kadubu",
    subtitle: "Steamed rice dumplings",
    body: "Soft, coconut-flecked steamed rice balls served with a spicy saagu, coriander chutney and red chilli chutney.",
    img: "/dish-kadubu.jpg",
  },
];

export const TESTIMONIALS = [
  {
    name: "Ananya R.",
    from: "Bengaluru",
    text:
      "The most magical monsoon weekend. Coffee at dawn, waterfalls at noon and bonfire under a million stars.",
  },
  {
    name: "Karthik & Meera",
    from: "Chennai",
    text:
      "Every detail is thought through. The Malnad thali alone is worth the drive from the city.",
  },
  {
    name: "Vikram S.",
    from: "Mumbai",
    text:
      "Booked the group cottage for 7 friends. Absolute privacy, jaw-dropping views, warm hospitality.",
  },
  {
    name: "Priya D.",
    from: "Hyderabad",
    text:
      "Peak Adventures gave us the Western Ghats we always dreamed of — wild, quiet and truly authentic.",
  },
  {
    name: "Rahul M.",
    from: "Pune",
    text:
      "Rain-soaked estate walks, native food, and the kindest hosts. Ten out of ten.",
  },
  {
    name: "Shruti K.",
    from: "Kochi",
    text:
      "Woke up to hornbills and misty peaks. The bonfire storytelling night was unforgettable.",
  },
];

// Editorial gallery ("Moments from the Ghats")
export const PHOTO_GALLERY = [
  {
    src: "https://customer-assets.emergentagent.com/job_western-ghats-escape/artifacts/rvhz1iu9_2.jpg",
    alt: "Aerial view of Peak Adventures Malnad ambience — red-roof cottage, pool and coffee-estate surrounds",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_western-ghats-escape/artifacts/4lreh4jt_5.jpg",
    alt: "Sit-out verandah with cane chairs overlooking the Malnad hills",
  },
  { src: "/gallery-1.jpg", alt: "Coffee estate walkway" },
  { src: "/gallery-2.jpg", alt: "Partner homestay evening" },
  { src: "/gallery-3.jpg", alt: "Estate morning light" },
  { src: "/gallery-4.jpg", alt: "Malnad landscape" },
];

export const ATTRACTIONS_MARQUEE = [
  { id: "jhari-falls", name: "Jhari Falls", img: "/attr-jhari.jpg" },
  { id: "hebbe-falls", name: "Hebbe Falls", img: "/attr-hebbe.jpg" },
  { id: "tea-estate", name: "Kelagur Tea Estate", img: "/attr-tea-estate.jpg" },
  { id: "mullayanagiri", name: "Mullayanagiri", img: "/attr-mullayanagiri.jpg" },
  { id: "manjarabad", name: "Manjarabad Fort", img: "/attr-manjarabad.jpg" },
  { id: "kudremukh", name: "Kudremukh National Park", img: "/attr-kudremukh.jpg" },
];

export const ATTRACTIONS = [
  {
    place: "Chikkamagaluru",
    title: "The Coffee Cradle of India",
    tagline: "The coffee cradle of India",
    groups: [
      {
        title: "Mountains & Viewpoints",
        items: [
          "Mullayanagiri — highest peak in Karnataka",
          "Baba Budangiri",
          "Manikyadhara Falls",
          "Jhari Waterfall (Buttermilk Falls)",
          "Z Point",
          "Seethalayanagiri",
          "Galikere Lake",
          "Hirekolale Lake",
          "Ayyanakere Lake",
        ],
      },
      {
        title: "Waterfalls",
        items: [
          "Hebbe Falls",
          "Kalhatti Falls",
          "Hanuman Gundi Falls",
          "Jhari Falls",
          "Manikyadhara Falls",
        ],
      },
      {
        title: "Nature & Wildlife",
        items: [
          "Kudremukh National Park",
          "Bhadra Wildlife Sanctuary",
          "Coffee plantations",
          "Coffee Museum",
        ],
      },
    ],
  },
  {
    place: "Kalasa",
    title: "Temple Town in the Mist",
    tagline: "Temple town in the mist",
    groups: [
      {
        title: "Temples",
        items: [
          "Kalaseshwara Temple",
          "Horanadu Annapoorneshwari Temple (10 km)",
        ],
      },
      {
        title: "Nature",
        items: [
          "Kalasa View Point",
          "River Bhadra",
          "Samse",
          "Kudremukh Base",
          "Soormane Falls",
          "Elaneeru Falls",
        ],
      },
    ],
  },
  {
    place: "Mudigere",
    title: "Our Home in the Hills",
    tagline: "Our home in the hills",
    groups: [
      {
        title: "Top Attractions",
        items: [
          "Devaramane View Point",
          "Devaramane Temple",
          "Ettina Bhuja Trek",
          "Ballalarayana Durga Fort",
          "Bandaje Falls Trek",
          "Charmadi Ghat",
          "Alekan Falls",
          "Kodige Falls",
          "Kelagur Tea Estate",
          "Coffee plantations",
        ],
      },
    ],
  },
  {
    place: "Sakleshpur",
    title: "Green Train Country",
    tagline: "Green train country",
    groups: [
      {
        title: "Viewpoints",
        items: ["Bisle View Point", "Jenukal Gudda"],
      },
      { title: "Historic", items: ["Manjarabad Fort"] },
      {
        title: "Waterfalls",
        items: [
          "Magajahalli Falls",
          "Mookanamane Falls",
          "Hadlu Falls",
        ],
      },
      {
        title: "Nature",
        items: [
          "Bisle Ghat",
          "Agani Peak",
          "Hemavathi Backwaters",
          "Coffee & pepper estates",
        ],
      },
    ],
  },
  {
    place: "Belur",
    title: "UNESCO Heritage & Serene Lakes",
    tagline: "UNESCO heritage & lakes",
    groups: [
      {
        title: "Heritage & Temples",
        items: [
          "Chennakesava Temple ★ (UNESCO)",
          "Veeranarayana Temple",
          "Madanikas (Chennakesava sculptures)",
          "Hoysala-era temples around Belur",
        ],
      },
      {
        title: "Lakes, Nature & Activities",
        items: [
          "Yagachi Dam & Backwaters",
          "Kapina Bagilu",
          "Yagachi River viewpoints",
          "Coffee plantations around Belur",
          "Boating at Yagachi Dam (seasonal)",
          "Heritage walk & handicraft shopping",
        ],
      },
    ],
  },
];

export const CURATED_LISTS = [
  {
    title: "Best Trekking",
    items: [
      "Mullayanagiri",
      "Kudremukh Peak",
      "Ettina Bhuja",
      "Ballalarayana Durga",
      "Bandaje Falls",
      "Z Point",
      "Jenukal Gudda",
      "Agani Peak",
      "Kurinjal Peak",
    ],
  },
  {
    title: "Best Waterfalls",
    items: [
      "Jhari Falls",
      "Hebbe Falls",
      "Hanuman Gundi Falls",
      "Manikyadhara Falls",
      "Bandaje Falls",
      "Alekan Falls",
      "Magajahalli Falls",
      "Mookanamane Falls",
      "Soormane Falls",
      "Elaneeru Falls",
      "Kalhatti Falls",
    ],
  },
  {
    title: "Best Lakes",
    items: [
      "Hirekolale Lake",
      "Galikere Lake",
      "Ayyanakere Lake",
      "Hemavathi Backwaters",
    ],
  },
  {
    title: "Best Temples",
    items: [
      "Kalaseshwara Temple",
      "Horanadu Annapoorneshwari Temple",
      "Sringeri Sharada Peetham",
      "Devaramane Temple",
      "Baba Budangiri Dargah",
    ],
  },
  {
    title: "Scenic Drives",
    items: [
      "Chikmagalur → Mullayanagiri",
      "Chikmagalur → Baba Budangiri",
      "Kalasa → Kudremukh",
      "Mudigere → Charmadi Ghat",
      "Sakleshpur → Bisle Ghat",
      "Sakleshpur → Manjarabad Fort",
      "Kalasa → Horanadu → Sringeri",
    ],
  },
  {
    title: "Hidden Gems",
    items: [
      "Devaramane View Point",
      "Samse Tea Estate",
      "Soormane Falls",
      "Kodige Falls",
      "Agani Peak",
      "Mookanamane Falls",
      "Kelagur Tea Estate",
      "Galikere Lake",
      "Ballalarayana Durga sunrise",
    ],
  },
];

export const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Stays", href: "#rooms" },
  { label: "Cuisine", href: "#cuisine" },
  { label: "Bonfire", href: "#bonfire" },
  { label: "Nearby", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

export const FAQS = [
  {
    q: "Where is Peak Adventures Malnad located?",
    a: "We are nestled in Mudigere, Chikkamagaluru district, Karnataka, with curated experiences across Kalasa, Chikkamagaluru and Sakleshpur.",
  },
  {
    q: "How do I book a stay?",
    a: "Tap the Book Now button — it takes you straight to our WhatsApp. Or call +91 77956 77115. Bookings are confirmed within 30 minutes.",
  },
  {
    q: "What is the best time to visit?",
    a: "September to February for cool sunny days, June to August for a lush monsoon experience with waterfalls in full flow.",
  },
  {
    q: "Are meals included?",
    a: "Yes — every stay includes an authentic Malnad breakfast, lunch and dinner cooked in our estate kitchen.",
  },
];
